import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import {
  AiSummaryFieldModal,
  type AiSummaryFieldModalProps,
  buildAiSummaryPrompt,
  useAiSummaryPreviewOptions,
  useIsAiSummaryFieldEnabled
} from '@databases/database-ai-fields';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { previewDatabaseAiField } from '@databases/rovo-bridge';
import React, {
  createContext,
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

type AiSummaryFieldConfigModalProps = Omit<
  AiSummaryFieldModalProps,
  'onClose' | 'onGeneratePreview' | 'onSaveAndRun'
> & {
  fieldName: string;
  onClose?: AiSummaryFieldModalProps['onClose'];
  onSaveAndRun?: (
    config: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0]
  ) => Promise<void>;
};

type DatabaseAiConfigModal = {
  type: 'ai-summary-field';
  props: AiSummaryFieldConfigModalProps;
};

export type { DatabaseAiConfigModal };

interface DatabaseAiConfigModalContextValue {
  openAiConfigModal: (modal: DatabaseAiConfigModal) => void;
  closeAiConfigModal: () => void;
}

interface DatabaseAiConfigModalProviderProps {
  children: ReactNode;
  contentId: string;
}

const DatabaseAiConfigModalContext =
  createContext<DatabaseAiConfigModalContextValue | null>(null);

const AI_SUMMARY_RUN_ERROR_FLAG_ID = 'ai-summary-run-error';

export const DatabaseAiConfigModalProvider: FunctionComponent<
  DatabaseAiConfigModalProviderProps
> = ({ children, contentId }) => {
  const { formatMessage, locale } = useIntl();
  const { addFlag, clearFlags, hideFlag, showFlag } = useFlags();
  const [activeModal, setActiveModal] = useState<DatabaseAiConfigModal | null>(null);
  const [isPreviewMenuOpen, setIsPreviewMenuOpen] = useState(false);
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const previewOptions = useAiSummaryPreviewOptions(
    contentId,
    activeModal?.type === 'ai-summary-field' && isPreviewMenuOpen
  );

  const closeAiConfigModal = useCallback(() => {
    setIsPreviewMenuOpen(false);
    setActiveModal(null);
  }, []);

  const openAiConfigModal = useCallback((modal: DatabaseAiConfigModal) => {
    setIsPreviewMenuOpen(false);
    setActiveModal(modal);
  }, []);

  const showAiSummaryRunErrorFlag = useCallback(
    (
      modal: DatabaseAiConfigModal,
      settings: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0]
    ) => {
      const flagId = `${AI_SUMMARY_RUN_ERROR_FLAG_ID}:${Date.now()}`;
      const usesBridgeFlags = isFeatureFlagEnabled('database-frontend-flags-over-bridge');
      const dismissFlag = () => {
        if (usesBridgeFlags) {
          hideFlag(flagId);
        } else {
          clearFlags();
        }
      };
      const tryAgain = () => {
        void (async () => {
          try {
            await modal.props.onSaveAndRun?.(settings);
            dismissFlag();
          } catch {
            // Keep the existing flag visible so the user can try again.
          }
        })();
      };
      const flag = {
        title: formatMessage(
          messages['field-settings.ai-summary.run-error-title.non-final']
        ),
        description: formatMessage(
          messages['field-settings.ai-summary.run-error-description.non-final']
        ),
        actions: [
          {
            content: formatMessage(
              messages['field-settings.ai-summary.run-error-try-again.non-final']
            ),
            onClick: tryAgain
          }
        ]
      };

      if (usesBridgeFlags) {
        showFlag({
          ...flag,
          id: flagId,
          type: 'error',
          appearance: 'normal',
          close: 'auto'
        });
      } else {
        addFlag({
          ...flag,
          appearance: 'normal',
          icon: () => (
            <StatusErrorIcon
              color={token('color.icon.danger', '#C9372C')}
              label=""
              spacing="spacious"
            />
          )
        });
      }
    },
    [addFlag, clearFlags, formatMessage, hideFlag, showFlag]
  );

  const value = useMemo(
    () => ({
      openAiConfigModal,
      closeAiConfigModal
    }),
    [closeAiConfigModal, openAiConfigModal]
  );

  return (
    <DatabaseAiConfigModalContext.Provider value={value}>
      {children}
      {isAiFieldsSummaryEnabled && activeModal?.type === 'ai-summary-field' && (
        <AiSummaryFieldModal
          mode={activeModal.props.mode}
          initialConfig={activeModal.props.initialConfig}
          previewOptions={previewOptions}
          onPreviewMenuOpen={() => setIsPreviewMenuOpen(true)}
          onClose={() => {
            activeModal.props.onClose?.();
            closeAiConfigModal();
          }}
          onRegenerate={activeModal.props.onRegenerate}
          onGeneratePreview={({ abortSignal, config, entryId }) =>
            previewDatabaseAiField({
              abortSignal,
              contentId,
              entryId,
              fieldName: activeModal.props.fieldName,
              locale,
              prompt: buildAiSummaryPrompt({
                operation: 'single-entry-generate',
                targetFieldName: activeModal.props.fieldName,
                config
              })
            })
          }
          onSaveAndRun={(settings) => {
            closeAiConfigModal();

            void (async () => {
              try {
                await activeModal.props.onSaveAndRun?.(settings);
              } catch {
                showAiSummaryRunErrorFlag(activeModal, settings);
              }
            })();
          }}
          onSaveWithoutRunning={(settings) => {
            activeModal.props.onSaveWithoutRunning?.(settings);
            closeAiConfigModal();
          }}
        />
      )}
    </DatabaseAiConfigModalContext.Provider>
  );
};

export const useDatabaseAiConfigModal = () => {
  const value = useContext(DatabaseAiConfigModalContext);

  if (!value) {
    throw new Error(
      'useDatabaseAiConfigModal must be used within DatabaseAiConfigModalProvider'
    );
  }

  return value;
};
